// The Overview page's panels, in one place.
//
// `type` is the id stored in each browser's saved layout (localStorage 'dragDropState'). The
// stored ids are the old numbered ones ('item1' ... ), so they must never be renamed; `key`
// is the readable name the page template switches on.

export interface OverviewLayoutItem {
  id: string;
  type: string;
}

export interface OverviewLayout {
  todo: OverviewLayoutItem[];
  inProgress: OverviewLayoutItem[];
  done: OverviewLayoutItem[];
}

export const OVERVIEW_PANELS: { [type: string]: { key: string, title: string } } = {
  item1: { key: 'property', title: 'Property Info' },
  item2: { key: 'inspection', title: 'Inspection' },
  item3: { key: 'schedule-inspection', title: 'Schedule New Inspection' },
  item4: { key: 'ordered-by', title: 'Ordered By' },
  item5: { key: 'interested-parties', title: 'Interested Parties' },
  item6: { key: 'reports', title: 'Reports/Documents' },
  item7: { key: 'accounting', title: 'Accounting' },
  item8: { key: 'job-cost', title: 'Job Costing' },
  item9: { key: 'notes', title: 'Notes' },
  item11: { key: 'action', title: 'Action' },
  // Home Inspection Notes (mockup 2026-10-29); shown only while home inspections are on, when
  // item9 reads "Termite Notes".
  item12: { key: 'home-notes', title: 'Home Inspection Notes' },
};

// 'item10' was the Order History panel; its View Logs link is now in the Notes panel footer.
const RETIRED_TYPES = ['item10'];

// Columns follow the mockup: property / inspection / re-inspection, then the people and the
// documents, then money, actions and notes.
export function defaultOverviewLayout(): OverviewLayout {
  return {
    todo: [
      { id: '1', type: 'item1' },
      { id: '2', type: 'item2' },
      { id: '3', type: 'item3' }
    ],
    inProgress: [
      { id: '4', type: 'item4' },
      { id: '5', type: 'item5' },
      { id: '6', type: 'item6' }
    ],
    done: [
      { id: '7', type: 'item7' },
      { id: '11', type: 'item11' },
      { id: '8', type: 'item8' },
      { id: '12', type: 'item12' },
      { id: '9', type: 'item9' }
    ]
  };
}

// Brings a layout saved by an older version up to date: drops retired or unknown panels and
// adds any panel the saved layout does not have (in its default column), keeping the
// user's order for everything else.
export function migrateOverviewLayout(saved: any): OverviewLayout {
  const columns: (keyof OverviewLayout)[] = ['todo', 'inProgress', 'done'];
  const layout: OverviewLayout = { todo: [], inProgress: [], done: [] };
  const seen: { [type: string]: boolean } = {};

  columns.forEach(col => {
    const items = saved && Array.isArray(saved[col]) ? saved[col] : [];
    items.forEach(item => {
      if (!item || !OVERVIEW_PANELS[item.type] || RETIRED_TYPES.indexOf(item.type) !== -1 || seen[item.type]) {
        return;
      }
      seen[item.type] = true;
      layout[col].push({ id: String(item.id), type: item.type });
    });
  });

  const defaults = defaultOverviewLayout();
  columns.forEach(col => {
    defaults[col].forEach((item, index) => {
      if (seen[item.type]) {
        return;
      }
      seen[item.type] = true;
      // The Action panel goes right after Accounting, wherever the user has put Accounting.
      if (item.type === 'item11') {
        const accountingCol = columns.find(c => layout[c].some(i => i.type === 'item7'));
        if (accountingCol) {
          const after = layout[accountingCol].findIndex(i => i.type === 'item7');
          layout[accountingCol].splice(after + 1, 0, item);
          return;
        }
      }
      // Home Inspection Notes goes right above the Notes panel (now Termite Notes), wherever it is.
      if (item.type === 'item12') {
        const notesCol = columns.find(c => layout[c].some(i => i.type === 'item9'));
        if (notesCol) {
          layout[notesCol].splice(layout[notesCol].findIndex(i => i.type === 'item9'), 0, item);
          return;
        }
      }
      layout[col].splice(Math.min(index, layout[col].length), 0, item);
    });
  });

  return layout;
}
